import { useMemo } from "react"

import { Box, type BoxProps } from "@mui/material"
import { styled } from "@mui/material/styles"
import parse, {
  attributesToProps,
  domToReact,
  type HTMLReactParserOptions,
} from "html-react-parser"

import { Link, type FC } from ".."

type ContentProps = BoxProps & {
  content: string
}

const parseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if ("name" in domNode && "attribs" in domNode) {
      const { attribs, name, children } = domNode

      if (name === "div" && attribs.class === "raw-html-embed") {
        return <>{domToReact(children, parseOptions)}</>
      }

      if (name === "iframe" && attribs.src.includes("youtube")) {
        let src = attribs.src
        if (src.includes("youtube.com/watch?v=")) {
          src = src.replace("youtube.com/watch?v=", "youtube.com/embed/")
        }

        const videoParams = new URLSearchParams({
          autoplay: "0",
          controls: "1",
          showinfo: "0",
          rel: "0",
          modestbranding: "1",
          playsinline: "1",
          enablejsapi: "1",
        })

        const videoSrc = `${src}?${videoParams}`

        return (
          <iframe
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...attributesToProps(attribs)}
            className="video"
            src={videoSrc}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )
      }

      if (name === "figure") {
        return (
          <figure
            /* eslint-disable-next-line react/jsx-props-no-spreading */
            {...attributesToProps(attribs)}
            className="img-with-caption"
          >
            {domToReact(children, parseOptions)}
          </figure>
        )
      }

      if (name === "a") {
        if (children.some((child) => child.type === "tag" && child.name === "img")) {
          return (
            <Link
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...attributesToProps({ ...attribs, target: "_self" })}
              type="base"
              sx={{ fontWeight: "inherit", pb: 0.5 }}
            >
              {domToReact(children, parseOptions)}
            </Link>
          )
        }
        return (
          <Link
            {...attributesToProps({ ...attribs, target: "_self" })}
            sx={{ fontWeight: "inherit", pb: 0.5 }}
          >
            {domToReact(children, parseOptions)}
          </Link>
        )
      }
    }
  },
}

export const ArticleBodyContent: FC<ContentProps> = ({ content, ...props }) => {
  const body = useMemo(() => {
    // const cleanHtmlEntities = decode(content).replace(/&[#0-9a-z]+;/gi, "")
    return parse(content || "", parseOptions)
  }, [content])

  return <ArticleBodySection {...props}>{body}</ArticleBodySection>
}

export const ArticleBodySection = styled(Box)(
  ({ theme }) => `
    margin: 0 auto;

    h2 {
      margin-top: ${theme.spacing(15)};
      margin-bottom: ${theme.spacing(10)};
      font-family: ${theme.typography["heading.xl"].fontFamily};
      font-size: ${theme.typography["heading.xl"].fontSize};
      font-weight: ${theme.typography["heading.xl"].fontWeight};
      line-height: ${theme.typography["heading.xl"].lineHeight};

      &:last-child {
        margin-bottom: 0;
      }

      ${theme.breakpoints.down("md")} {
        margin-top: ${theme.spacing(12)};
        font-size: ${theme.spacing(6)};
      }
    }

    h3 {
      margin-bottom: ${theme.spacing(5)};
      font-family: ${theme.typography["heading.sm"].fontFamily};
      font-size: ${theme.typography["heading.sm"].fontSize};
      font-weight: ${theme.typography["heading.sm"].fontWeight};
      line-height: ${theme.typography["heading.sm"].lineHeight};

      &:last-child {
        margin-bottom: 0;
      }
    }

    b, strong {
      font-weight: 500;
    }
    
    p {
      margin-bottom: ${theme.spacing(5)};
      font-family: ${theme.typography["body.md"].fontFamily};
      font-size: ${theme.typography["body.md"].fontSize};
      font-weight: ${theme.typography["body.md"].fontWeight};
      line-height: ${theme.typography["body.md"].lineHeight};
      ${theme.breakpoints.down("md")} {
        font-size: ${theme.spacing(3)};
        line-height: 1.8;
      }
    }
    
    img {
      max-width: 100%;
      display: flex;
      height: auto;
      margin-top: ${theme.spacing(15)};
      margin-bottom: ${theme.spacing(15)};
      border-radius: ${theme.spacing(1.5)};

      .align-center {
        margin-left: auto;
        margin-right: auto;
      }

      ${theme.breakpoints.down("md")} {
        margin-top: ${theme.spacing(6)};
        margin-bottom: ${theme.spacing(6)};
      }
    }

    ul {
      margin-top: ${theme.spacing(15)};
      margin-bottom: ${theme.spacing(15)};
      list-style-type: none;
      padding-left: 0;
      
      li {
        font-family: ${theme.typography["body.md"].fontFamily};  
        font-size: ${theme.typography["body.md"].fontSize};
        font-weight: ${theme.typography["body.md"].fontWeight};
        line-height: ${theme.typography["body.md"].lineHeight};
        margin-bottom: ${theme.spacing(5)};
        
        &::before {
          content: url(\"data:image/svg+xml,%3Csvg width='6' height='8' viewBox='0 0 6 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.387618 0.513662L5.4353 3.82942C5.52157 3.88616 5.52157 4.11347 5.4353 4.17058L0.387618 7.48634C0.344713 7.51434 0.295842 7.4985 0.263032 7.44618L0.0335917 7.07776C9.34775e-05 7.02397 -0.00931359 6.94292 0.00972993 6.87366L0.792579 4L0.00972968 1.12634C0.00330536 1.10276 9.32381e-05 1.07771 9.3237e-05 1.05266C9.32349e-05 1.00476 0.0117947 0.957606 0.0335915 0.922238L0.263031 0.55382C0.295612 0.501504 0.344712 0.485663 0.387618 0.513662Z' fill='%23F05B2A'/%3E%3C/svg%3E%0A\");
          color: ${theme.palette["primary.800"].main};
          display: inline-block;
          width: ${theme.spacing(5)};
        }
      }

      ${theme.breakpoints.down("md")} {
        margin-top: ${theme.spacing(8)};
        margin-bottom: ${theme.spacing(8)};
        
        li {
          font-size: ${theme.spacing(3)};
          line-height: 1.8;
        }
      }
    }

    .video, figure, blockquote {
      margin-top: ${theme.spacing(15)};
      margin-bottom: ${theme.spacing(15)};

      ${theme.breakpoints.down("md")} {
        margin-top: ${theme.spacing(10)};
        margin-bottom: ${theme.spacing(10)};
      }
    }

    .video {
      aspect-ratio: 16 / 9;
      border: 0;
      border-radius: ${theme.spacing(1.5)};
      display: block;
      height: 100%;
      width: 100%;
    }

    figure {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;

      ${theme.breakpoints.down("xl")} {
        width: auto;
        margin: 24px 0;
      }

      & > br {
        display: none;
      }

      & > img {
        width: 140%;
        max-width: none;
        height: auto;
        border-radius: ${theme.spacing(1.5, 1.5, 0, 0)};
        margin: 0!important;

        ${theme.breakpoints.down("xl")} {
          width: 100%;
        }
      }

      & > a {
        display: block;
        width: 140%;
        padding-bottom: 0;

        img {
          height: auto;
          border-radius: ${theme.spacing(1.5, 1.5, 0, 0)};
          margin: 0;
        }

        ${theme.breakpoints.down("xl")} {
          width: 100%;
        }
      }

      figcaption {
        width: 140%;
        border: 1px solid ${theme.palette["neutral.200"].main};
        border-top: 0;
        border-radius: ${theme.spacing(0, 0, 1.5, 1.5)};
        font-family: ${theme.typography["body.xs"].fontFamily};  
        font-size: 11px;
        font-weight: 500;
        line-height: ${theme.typography["body.xs"].lineHeight};
        padding: ${theme.spacing(5, 4)};
        margin-top: -2px;

        ${theme.breakpoints.down("xl")} {
          font-size: 9px;
          padding: ${theme.spacing(4)};
          width: 100%;
        }
      }
    }

    blockquote {
      border-left: 2px solid ${theme.palette["primary.800"].main};
      padding: ${theme.spacing(10)};

      img {
        margin: ${theme.spacing(0, 0, 5)}!important;
      }

      p:last-child {
        margin-bottom: 0;
      }

      ${theme.breakpoints.down("md")} {
        padding: ${theme.spacing(7)};
      }
    }    
  `
)
